import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { API_BASE_URL } from '@/shared/lib/constants'

// Create axios instance
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Token storage keys
const ACCESS_TOKEN_KEY = 'tonal_access_token'
const REFRESH_TOKEN_KEY = 'tonal_refresh_token'

// Intentar recuperar JWT desde sesión Django activa antes de redirigir a login
let isRecoveringSession = false
async function trySessionRecovery(): Promise<boolean> {
  if (isRecoveringSession) return false
  isRecoveringSession = true
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/session-to-jwt/`)
    const { access, refresh } = response.data
    tokenStorage.setTokens(access, refresh)
    return true
  } catch {
    return false
  } finally {
    isRecoveringSession = false
  }
}

// Token management
export const tokenStorage = {
  getAccessToken: () => localStorage.getItem(ACCESS_TOKEN_KEY),
  getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  setTokens: (access: string, refresh: string) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, access)
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh)
  },
  clearTokens: () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
  },
}

// Request interceptor - add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = tokenStorage.getAccessToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor - handle token refresh
let isRefreshing = false
let failedQueue: Array<{
  resolve: (token: string) => void
  reject: (error: AxiosError) => void
}> = []

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error)
    } else if (token) {
      promise.resolve(token)
    }
  })
  failedQueue = []
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // Skip token refresh for auth endpoints (login, refresh)
    const isAuthEndpoint = originalRequest.url?.includes('/auth/jwt/')

    // If error is 401 and we haven't retried yet and it's not an auth endpoint
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`
              resolve(apiClient(originalRequest))
            },
            reject: (err: AxiosError) => {
              reject(err)
            },
          })
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      const refreshToken = tokenStorage.getRefreshToken()

      if (!refreshToken) {
        // Sin refresh token: intentar recuperar desde sesión Django
        const recovered = await trySessionRecovery()
        if (recovered) {
          const newToken = tokenStorage.getAccessToken()!
          processQueue(null, newToken)
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          isRefreshing = false
          return apiClient(originalRequest)
        }
        tokenStorage.clearTokens()
        isRefreshing = false
        window.location.href = '/login'
        return Promise.reject(error)
      }

      try {
        const response = await axios.post(`${API_BASE_URL}/auth/jwt/refresh`, {
          refresh: refreshToken,
        })

        const { access } = response.data
        tokenStorage.setTokens(access, refreshToken)

        processQueue(null, access)

        originalRequest.headers.Authorization = `Bearer ${access}`
        return apiClient(originalRequest)
      } catch (refreshError) {
        // Refresh falló: intentar recuperar desde sesión Django
        const recovered = await trySessionRecovery()
        if (recovered) {
          const newToken = tokenStorage.getAccessToken()!
          processQueue(null, newToken)
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          return apiClient(originalRequest)
        }
        processQueue(refreshError as AxiosError, null)
        tokenStorage.clearTokens()
        window.location.href = '/login'
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    // Handle 401 on auth endpoints - only attempt session recovery for jwt/refresh
    // Never retry login or session-to-jwt (would cause infinite loop on bad credentials)
    if (error.response?.status === 401 && isAuthEndpoint) {
      if (originalRequest.url?.includes('/auth/jwt/refresh')) {
        const recovered = await trySessionRecovery()
        if (recovered) return apiClient(error.config as InternalAxiosRequestConfig)
      }
      tokenStorage.clearTokens()
      window.location.href = '/login'
      return Promise.reject(error)
    }

    return Promise.reject(error)
  }
)

export default apiClient
